import { useState, useEffect } from 'react';
import { supabase } from '../../integrations/supabase/client';
import { usePlatformTenant } from '../../contexts/PlatformTenantContext';
import { useToast } from '../../contexts/ToastContext';

interface PlatformTenantOption {
  id: string;
  company_name: string;
  subdomain: string;
}

interface PlatformTenantSelectorProps {
  compact?: boolean;
}

/**
 * Fix Gap #7: Platform Tenant Selector for Super Admins
 * Allows super admins to switch context between different platform tenants
 */
export const PlatformTenantSelector: React.FC<PlatformTenantSelectorProps> = ({ compact = false }) => {
  const { platformTenant, isSuperAdmin, refresh } = usePlatformTenant();
  const { showSuccess, showError } = useToast();
  const [tenants, setTenants] = useState<PlatformTenantOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');

  useEffect(() => {
    const loadTenants = async () => {
      // Skip if not super admin - don't need session check here
      if (!isSuperAdmin) {
        setLoading(false);
        return;
      }

      // Restore selected tenant from localStorage for super admins
      const savedTenantId = localStorage.getItem('super_admin_selected_tenant_id');
      if (savedTenantId) {
        // Restoring saved tenant selection
        setSelectedTenantId(savedTenantId);
      } else {
        // Default to Platform View when no tenant is selected
        setSelectedTenantId('__platform__');
      }
      await fetchAllPlatformTenants();
    };

    loadTenants();
    
    if (platformTenant) {
      setSelectedTenantId(platformTenant.id);
    } else if (isSuperAdmin) {
      // Ensure Platform View is selected when no tenant
      setSelectedTenantId('__platform__');
    }
  }, [isSuperAdmin, platformTenant]);

  const fetchAllPlatformTenants = async () => {
    try {
      // Fetching platform tenants for super admin
      
      const { data, error } = await supabase
        .from('platform_tenants')
        .select('id, company_name, subdomain')
        .order('company_name');

      if (error) {
        console.error('Error from Supabase:', error);
        throw error;
      }
      
      // Platform tenants fetched
      setTenants(data || []);
    } catch (err) {
      console.error('Error fetching platform tenants:', err);
      showError('Failed to load platform tenants', 'Error fetching tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleTenantChange = async (tenantId: string) => {
    try {
      setSelectedTenantId(tenantId);
      
      // Special handling for Platform View
      if (tenantId === '__platform__') {
        localStorage.removeItem('super_admin_selected_tenant_id');
      } else {
        localStorage.setItem('super_admin_selected_tenant_id', tenantId);
      }

      // Single event dispatch - context listens and refreshes
      window.dispatchEvent(new CustomEvent('tenant-switch'));
      
      const tenant = tenants.find(t => t.id === tenantId);
      const message = tenantId === '__platform__' 
        ? 'Now viewing platform management'
        : `Now viewing ${tenant?.company_name || 'selected tenant'}`;
      showSuccess(tenantId === '__platform__' ? 'Switched to Platform View' : 'Switched Platform Tenant', message);
    } catch (err) {
      console.error('Error switching platform tenant:', err);
      showError('Switch Failed', 'Failed to switch platform tenant');
      localStorage.removeItem('super_admin_selected_tenant_id');
      setSelectedTenantId('__platform__');
    }
  };

  // Only show for super admins
  if (!isSuperAdmin) {
    return null;
  }

  if (loading) {
    return compact ? (
      <div className="text-xs text-muted-foreground">Loading...</div>
    ) : (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
        <span>Loading tenants...</span>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground whitespace-nowrap">
          Platform:
        </label>
        <select
          value={selectedTenantId}
          onChange={(e) => handleTenantChange(e.target.value)}
          className="px-2 py-1 text-sm border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:outline-none min-w-[180px]"
          style={{ backgroundColor: 'hsl(var(--background))', color: 'hsl(var(--foreground))' }}
        >
          <option value="__platform__" className="bg-background text-foreground font-semibold">🏢 Platform View</option>
          <option disabled className="bg-background text-foreground">───────────────</option>
          {tenants.map((tenant) => (
            <option key={tenant.id} value={tenant.id} className="bg-background text-foreground">
              {tenant.company_name}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 border-b border-border">
      <label className="text-xs text-muted-foreground mb-1 block">
        Platform Tenant
      </label>
      <select
        value={selectedTenantId}
        onChange={(e) => handleTenantChange(e.target.value)}
        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
        style={{ backgroundColor: 'hsl(var(--background))', color: 'hsl(var(--foreground))' }}
      >
        <option value="__platform__" className="bg-background text-foreground font-semibold">🏢 Platform View</option>
        <option disabled className="bg-background text-foreground">───────────────</option>
        {tenants.map((tenant) => (
          <option key={tenant.id} value={tenant.id} className="bg-background text-foreground">
            {tenant.company_name} ({tenant.subdomain})
          </option>
        ))}
      </select>
    </div>
  );
};